import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  X,
  StickyNote,
  ShieldAlert,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";

export function MiniStat({ label, value, accent = "default" }) {
  const ring =
    accent === "red"
      ? "border-red-400/40 bg-red-500/10 text-red-200"
      : "border-white/10 bg-white/[0.04] text-white";
  return (
    <div className={`rounded-xl p-2.5 border ${ring}`} data-testid={`vendor-mini-stat-${label.toLowerCase()}`}>
      <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-black">{label}</p>
      <p className="text-base font-black tracking-tight">{value ?? 0}</p>
    </div>
  );
}

export function VendorDeepProfile({ vendorId, onClose, isCEO }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [noteKind, setNoteKind] = useState("note");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [full, ns] = await Promise.all([
        api.get(`/admin/vendors/${vendorId}/full`),
        api.get(`/admin/vendors/${vendorId}/notes`),
      ]);
      setData(full.data);
      setNotes(ns.data.notes || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load profile");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [vendorId, onClose]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/vendors/${vendorId}/notes`, { body: newNote.trim(), kind: noteKind });
      toast.success(noteKind === "warning" ? "Warning logged · vendor notified" : "Internal note saved");
      setNewNote("");
      setNoteKind("note");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save note");
    }
  };

  const deleteNote = async (n) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/admin/vendors/notes/${n.note_id}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't delete");
    }
  };

  const setTrust = async (level) => {
    try {
      await api.post(`/admin/vendors/${vendorId}/trust`, { trust_level: level });
      toast.success(`Trust set to ${level}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't update");
    }
  };

  const v = data?.vendor;
  const stats = data?.stats || {};
  const profile = v?.vendor_profile || {};
  const riskColor = data?.auto_risk === "critical" || data?.auto_risk === "high"
    ? "border-red-400/40 bg-red-500/10 text-red-200"
    : data?.auto_risk === "medium"
    ? "border-amber-400/40 bg-amber-500/10 text-amber-200"
    : "border-emerald-400/30 bg-emerald-500/10 text-emerald-200";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center px-4 py-8" onClick={onClose} data-testid="vendor-deep-profile-modal">
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-gradient-to-br from-zinc-900 to-black border border-pink-400/30 shadow-[0_0_60px_-10px_rgba(236,72,153,0.4)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-black/70 backdrop-blur flex items-center gap-3">
          <h2 className="text-base font-black tracking-tight truncate flex-1">
            {profile.business_name || v?.name || "Vendor"}
          </h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center" data-testid="vendor-profile-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading || !v ? (
          <p className="py-12 text-center text-zinc-500 text-sm">Loading vendor…</p>
        ) : (
          <div className="p-5 space-y-5">
            {/* Identity card */}
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-2xl bg-pink-500/15 grid place-items-center text-pink-200 font-black text-lg shrink-0">
                {(profile.business_name || v.name).slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold truncate">{v.email}</p>
                <p className="text-xs text-zinc-400 truncate">{profile.vendor_type || "—"} · {profile.location || "no location"}</p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {v.is_verified && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-cyan-400/15 text-cyan-200 border border-cyan-400/30 font-bold">Verified</span>}
                  {v.is_vip && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-300 to-pink-500 text-black font-black">VIP</span>}
                  {v.is_suspended && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/15 text-red-200 border border-red-400/30 font-bold">Suspended</span>}
                  {v.is_banned && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/30 text-red-100 border border-red-400/50 font-black">Banned</span>}
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-bold ${riskColor}`} data-testid="vendor-auto-risk">
                    Risk: {data.auto_risk}
                  </span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2">
              <MiniStat label="Products" value={`${stats.active_products}/${stats.product_count}`} />
              <MiniStat label="Orders" value={stats.total_orders} />
              <MiniStat label="Completed" value={stats.completed_orders} />
              <MiniStat label="Revenue" value={`$${(stats.revenue || 0).toFixed(2)}`} />
              <MiniStat label="Tips" value={`$${(stats.tips_total || 0).toFixed(2)}`} />
              <MiniStat label="Rating" value={stats.avg_rating ? `${stats.avg_rating}★ (${stats.review_count})` : "—"} />
              <MiniStat label="Followers" value={stats.followers} />
              <MiniStat label="Warnings" value={stats.warnings_count} accent={stats.warnings_count > 0 ? "red" : "default"} />
              <MiniStat label="Cancelled" value={stats.cancelled_orders} />
            </div>

            {/* Trust override */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-black mb-2 inline-flex items-center gap-1.5">
                <ShieldAlert className="w-3 h-3" /> Staff trust override
                <span className="text-zinc-500 font-bold ml-1">(current: {data.trust_level})</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {["low", "medium", "high", "unset"].map((lvl) => (
                  <button
                    key={lvl}
                    onClick={() => setTrust(lvl)}
                    className={`rounded-full h-8 px-3 text-xs font-bold uppercase tracking-widest border ${
                      data.trust_level === lvl
                        ? "bg-pink-500/20 text-pink-200 border-pink-400/40"
                        : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
                    }`}
                    data-testid={`vendor-trust-${lvl}`}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes / warnings */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-black inline-flex items-center gap-1.5">
                <StickyNote className="w-3 h-3" /> Internal notes &amp; warnings ({notes.length})
              </p>
              <div className="space-y-2">
                <Textarea
                  rows={2}
                  placeholder="Add a private note or formal warning…"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="bg-white/5 border-white/10 text-sm rounded-xl"
                  data-testid="vendor-note-input"
                />
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded-full border border-white/10 bg-white/5 overflow-hidden">
                    <button
                      onClick={() => setNoteKind("note")}
                      className={`h-8 px-3 text-xs font-bold uppercase tracking-widest ${noteKind === "note" ? "bg-pink-500/20 text-pink-200" : "text-zinc-400"}`}
                      data-testid="vendor-note-kind-note"
                    >
                      Note
                    </button>
                    <button
                      onClick={() => setNoteKind("warning")}
                      className={`h-8 px-3 text-xs font-bold uppercase tracking-widest ${noteKind === "warning" ? "bg-red-500/25 text-red-100" : "text-zinc-400"}`}
                      data-testid="vendor-note-kind-warning"
                    >
                      ⚠ Warning
                    </button>
                  </div>
                  <Button
                    onClick={addNote}
                    disabled={!newNote.trim()}
                    className="ml-auto rounded-full h-8 px-4 text-xs bg-pink-500/20 hover:bg-pink-500/30 text-pink-100 border border-pink-400/40 font-bold"
                    data-testid="vendor-note-save"
                  >
                    Save
                  </Button>
                </div>
              </div>
              {notes.length === 0 ? (
                <p className="text-center text-zinc-500 text-xs py-3">No notes yet</p>
              ) : (
                <div className="space-y-2">
                  {notes.map((n) => (
                    <div
                      key={n.note_id}
                      className={`rounded-xl p-2.5 border ${n.kind === "warning" ? "bg-red-500/[0.08] border-red-400/30" : "bg-white/[0.04] border-white/10"}`}
                      data-testid={`vendor-note-${n.note_id}`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        {n.kind === "warning" && <AlertTriangle className="w-3 h-3 text-red-300" />}
                        <p className="text-xs font-black">{n.author_name}</p>
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/10 text-zinc-300 font-bold">
                          {(n.author_role || "").replace("_", " ")}
                        </span>
                        <span className="text-[10px] text-zinc-500 ml-auto">{new Date(n.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-words">{n.body}</p>
                      {(isCEO || n.author_id === undefined) && (
                        <button onClick={() => deleteNote(n)} className="mt-1 text-[10px] text-red-300/70 hover:text-red-200" data-testid={`vendor-note-delete-${n.note_id}`}>
                          Delete
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mod history */}
            {data.mod_history?.length > 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-black mb-2 inline-flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3" /> Recent moderation history
                </p>
                <div className="space-y-1.5">
                  {data.mod_history.slice(0, 8).map((m) => (
                    <div key={m.log_id} className="text-[11px] text-zinc-400 flex gap-2">
                      <span className="text-zinc-500 shrink-0">{new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                      <span className="font-bold text-pink-300">{m.action}</span>
                      <span className="text-zinc-500">by {m.actor_name}</span>
                      {m.reason && <span className="text-zinc-400 truncate">— {m.reason}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
